import React, { useState } from 'react';
import { Container, Typography, Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  // General FAQs (8 questions)
  {
    category: 'General',
    question: 'What is IndeeClips?',
    answer: 'IndeeClips is a free video streaming platform built specifically for Indian creators and viewers. We support regional languages, offer creator-friendly monetization, and prioritize India-first content over global viral trends.',
  },
  {
    category: 'General',
    question: 'Is IndeeClips free to use?',
    answer: 'Yes, IndeeClips is completely free for viewers and creators. You can watch unlimited videos, subscribe to channels, and upload content without any charges. We plan to offer a premium subscription (IndeeClips Premium) in the future for ad-free viewing and additional features.',
  },
  {
    category: 'General',
    question: 'How is IndeeClips different from YouTube?',
    answer: 'IndeeClips is built for India with Hindi-first interface, regional language support, lower monetization barriers (1K subs vs 10K), better revenue split (60/40 vs 55/45), and transparent algorithm changes. We prioritize Indian creators and hyperlocal content over global trends.',
  },
  {
    category: 'General',
    question: 'What languages are supported?',
    answer: 'Currently, IndeeClips supports English and Hindi for the interface. We are adding Tamil, Telugu, Marathi, Gujarati, Kannada, and Bengali by December 2026. You can watch and upload videos in any language.',
  },
  {
    category: 'General',
    question: 'Is there a mobile app?',
    answer: 'Yes, IndeeClips has mobile apps for both Android and iOS. Download from Google Play Store or Apple App Store. The mobile app is optimized for Indian networks and works smoothly even on 3G/4G connections.',
  },
  {
    category: 'General',
    question: 'How do I contact support?',
    answer: 'Email contact@indeeclips.com for general help. For creator-specific inquiries, email contact@indeeclips.com. We typically respond within 24-48 hours. Check our Help Center first for instant answers to common questions.',
  },
  {
    category: 'General',
    question: 'Is IndeeClips available outside India?',
    answer: 'Yes, IndeeClips works globally. However, our content and features are optimized for Indian audiences. Some features like monetization and localized recommendations may be India-focused.',
  },
  {
    category: 'General',
    question: 'How does IndeeClips make money?',
    answer: 'IndeeClips earns revenue primarily through advertising. We share 60% of ad revenue with creators (higher than most platforms). In the future, we will offer premium subscriptions and facilitate brand partnerships.',
  },

  // Account FAQs (7 questions)
  {
    category: 'Account',
    question: 'How do I create an account?',
    answer: 'Click "Sign Up" in the top-right corner, enter your name, email, password, and birthdate, then verify your email. Your account is created instantly and you can start watching and subscribing immediately.',
  },
  {
    category: 'Account',
    question: 'I forgot my password, what should I do?',
    answer: 'Click "Forgot Password" on the login page, enter your registered email, and we will send a password reset link. Click the link, create a new password, and log in. Reset links expire after 24 hours.',
  },
  {
    category: 'Account',
    question: 'Can I have multiple channels?',
    answer: 'Currently, each account can create one channel. If you need multiple channels (e.g., separate personal and business channels), create separate accounts with different email addresses.',
  },
  {
    category: 'Account',
    question: 'How do I delete my account?',
    answer: 'Go to Settings → Account → Delete Account. This permanently removes your profile, watch history, subscriptions, and uploaded videos. This action cannot be undone. Download your data first if you want to keep it.',
  },
  {
    category: 'Account',
    question: 'Is my data safe?',
    answer: 'Yes. IndeeClips uses HTTPS encryption for all data transfer, AES-256 encryption for stored data, and follows industry-standard security practices. We do not sell your personal data to third parties. Read our Privacy Policy for details.',
  },
  {
    category: 'Account',
    question: 'Can I change my username?',
    answer: 'You can change your display name anytime in Settings → Profile. However, your account email cannot be changed after registration. If you need a different email, create a new account.',
  },
  {
    category: 'Account',
    question: 'Why do I need to verify my email?',
    answer: 'Email verification ensures account security, enables password recovery, and confirms you are a real person (not a bot). Unverified accounts have limited features like restricted commenting and no upload privileges.',
  },

  // Content FAQs (8 questions)
  {
    category: 'Content',
    question: 'What types of videos can I upload?',
    answer: 'You can upload original content including vlogs, tutorials, reviews, music, comedy, educational videos, and more. Content must comply with our community guidelines—no hate speech, violence, adult content, or copyright violations.',
  },
  {
    category: 'Content',
    question: 'What is the maximum video size?',
    answer: 'Maximum file size is 5GB per video. For best results, compress videos to under 2GB. We recommend 1080p resolution at 5-10 Mbps bitrate. Larger files take longer to upload and process.',
  },
  {
    category: 'Content',
    question: 'How long can clips be?',
    answer: 'IndeeClips Clips (short-form vertical videos) can be 15 to 90 seconds long. They must be vertical format (9:16 aspect ratio). Regular videos have no maximum length but must be at least 1 minute.',
  },
  {
    category: 'Content',
    question: 'Why was my video removed?',
    answer: 'Videos are removed for violating community guidelines: adult content, violence, hate speech, spam, copyright infringement, or misleading metadata. Check your email for removal reason. You can appeal if you believe removal was a mistake.',
  },
  {
    category: 'Content',
    question: 'Can I download videos?',
    answer: 'Downloading is not currently available but coming soon (December 2026). Downloaded videos will expire after 48 hours to prevent piracy. Creators can disable downloads on their videos if they choose.',
  },
  {
    category: 'Content',
    question: 'What video formats are supported?',
    answer: 'Supported formats: MP4, MOV, AVI, MKV, FLV, WebM. We recommend MP4 with H.264 codec for best compatibility. Audio: AAC or MP3. Videos are automatically transcoded to multiple qualities after upload.',
  },
  {
    category: 'Content',
    question: 'How do I make my video private?',
    answer: 'When uploading or editing a video, set visibility to "Private" (only you can see), "Unlisted" (anyone with link can see), or "Public" (visible to everyone). Change anytime in video settings.',
  },
  {
    category: 'Content',
    question: 'Can I edit a video after uploading?',
    answer: 'You can edit title, description, thumbnail, and privacy settings after upload. You cannot edit the video file itself—you must delete and re-upload if you need to change the actual video content.',
  },

  // Creator FAQs (7 questions)
  {
    category: 'Creator',
    question: 'How do I monetize my content?',
    answer: 'IndeeClips monetization program launches November 2026. Requirements: 1,000 subscribers and 10,000 video views in last 90 days. Revenue split: 60% to creators, 40% to platform. Weekly payouts once you reach ₹1,000 threshold.',
  },
  {
    category: 'Creator',
    question: 'How do I get more subscribers?',
    answer: 'Create consistent, high-quality content in a specific niche. Optimize titles, thumbnails, and descriptions for search. Engage with comments. Upload regularly (3x/week minimum). Collaborate with other creators. Use IndeeClips Clips to reach new audiences.',
  },
  {
    category: 'Creator',
    question: 'Can I see who viewed my videos?',
    answer: 'No, viewer identities are private. You can see total view count, watch time, traffic sources, and demographics (age, gender, location) in your analytics dashboard, but not individual viewer names.',
  },
  {
    category: 'Creator',
    question: 'How do I verify my channel?',
    answer: 'Channel verification (checkmark badge) is currently invitation-only for channels with 10K+ subscribers. We verify identity, content authenticity, and adherence to guidelines. Public verification applications coming in 2027.',
  },
  {
    category: 'Creator',
    question: 'What are the content guidelines?',
    answer: 'No adult content, extreme violence, hate speech, harassment, spam, or copyright violations. Content must be original or properly licensed. Educational/documentary content with sensitive material is allowed if clearly labeled. See full guidelines at indeeclips.com/guidelines.',
  },
  {
    category: 'Creator',
    question: 'Can I delete comments on my videos?',
    answer: 'Yes. As a creator, you can delete any comment on your videos, block users from commenting, pin comments, or disable comments entirely. Go to video settings → Comments to manage comment permissions.',
  },
  {
    category: 'Creator',
    question: 'How long does video processing take?',
    answer: 'Most videos process in 1-5 minutes. 4K or very long videos may take 10-15 minutes. You will receive a notification when processing is complete and video is live. Processing converts your video to multiple quality levels.',
  },
];

const FAQ: React.FC = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const categories = ['General', 'Account', 'Content', 'Creator'];

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Box
        sx={{
          background: (theme) =>
            `linear-gradient(160deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          borderBottom: 1,
          borderColor: 'divider',
          py: 8,
          px: 3,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 3 }}>
            FAQ
          </Typography>
          <Typography variant="h2" component="h1" sx={{ my: 2, fontWeight: 700 }}>
            {t('faq.title', 'Frequently Asked Questions')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300, maxWidth: '560px' }}>
            Find quick answers to common questions about IndeeClips
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 8 }}>
        {categories.map((category) => (
          <Box key={category} sx={{ mb: 6 }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
              {category}
            </Typography>
            {faqData
              .filter((item) => item.category === category)
              .map((item, index) => (
                <Accordion
                  key={`${category}-${index}`}
                  expanded={expanded === `${category}-${index}`}
                  onChange={handleChange(`${category}-${index}`)}
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 2,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        my: 2,
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
          </Box>
        ))}

        <Box
          sx={{
            mt: 8,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Still Have Questions?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Can't find the answer you're looking for? Visit our Help Center or contact our support team.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default FAQ;
